import { IAppComponentProps } from '../../components';
import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, notification, Row, Select, Space, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import {
    ArrowLeftOutlined,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    FolderOutlined,
    FolderViewOutlined,
    FormOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { getI18n } from '../../utils/Utils';
import dayjs from 'dayjs';
import RenderIfEntitled from '../../components/EntitlementComponents';
import { SurveyCreateRequest, SurveyOverviewFilter } from '__generated__/globalTypes';
import { createSurveyMutation, getSurveyOverviewData } from './SurveyUtils';
import { PartnerLookup } from '../../components/PartnerLookup';
import { SurveyStatus } from '../../components/SurveyStatus';
import { CompanyLookup } from '../../components/CompanyLookup';
import { REMOVE_SELECTED_PARTNER_TO_SURVEY_PROCESS } from './surveyMasterData/queries';
import { showErrorNotification } from '../Notification';
import client from '../../config/GraphQLApolloClient';
import { addSelectedSurveyMasterData } from './surveyMasterData/SurveyMasterDataUtils';
import { PARTNER_TYPE, SELECTED_GROUP_ID } from '../../constants';
import _ from 'lodash';
import { SelectedSurveyGroupID } from './SurveyOverview';
import { useAppDispatch, useAppSelector } from '../../main/hooks';
import { MODE } from './IStoredSurvey';
import {
    DESTROY_SURVEY_OVERVIEW,
    destroyState,
    resetSurveyDataLoading,
    setSurveyDataLoading,
    setSurveyMode,
    updateSurveyOverviewCriteria,
} from './IStoredSurveySlice';
import { getDateFormatOrDefault } from '../users/UserFormatPreferenceServices';
import { useNavigate } from 'react-router-dom';
import styles from '../info-portal/components/styling/infoportal.module.css';
import './styling/SurveyOverviewSearchForm.css';

type SurveyOverviewSearchFormProps = {
    setSurveyOverviewData: any;
    onRefresh: any;
    selectedSurveyIds: any;
    showCopyButton?: boolean;
    copyMasterSurvey?: () => void;
    searchFormRef?: React.MutableRefObject<any>;
} & IAppComponentProps;

const SurveyOverviewSearchForm: React.FunctionComponent<SurveyOverviewSearchFormProps> = (props) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const intl = useIntl();
    const navigate = useNavigate();
    const Survey_Overview_Entitlement = 'display@survey';
    const [_partnerNo, setPartnerNo] = useState(undefined);
    const [partnerType, setPartnerType] = useState<string>();
    const surveyState = useAppSelector((state) => state.survey);

    const isOverViewMode = () => {
        return surveyState.survey.surveyMode === MODE.OVERVIEW ? true : false;
    };

    const fetchSurveyData = (surveyOverviewFilter: SurveyOverviewFilter) => {
        //
        console.dir(`surveyOverviewFilter : ${surveyOverviewFilter}`);
        dispatch(updateSurveyOverviewCriteria(surveyOverviewFilter));
        //
        dispatch(setSurveyDataLoading(true));
        Promise.resolve(getSurveyOverviewData(surveyOverviewFilter))
            .then((data) => {
                //props.setSurveyLoading(false);
                props.setSurveyOverviewData(data);
            })
            .catch(() => {
                dispatch(resetSurveyDataLoading(true));
            })
            .finally(() => {
                dispatch(setSurveyDataLoading(false));
            });
    };

    const handleSearchWithGroupId = (groupId: string) => {
        // Clear form and set groupId
        form.resetFields();
        form.setFieldsValue({ groupId });

        // Create search filter with groupId
        const surveyOverviewFilter: SurveyOverviewFilter = {
            groupId,
        };

        // Fetch data
        fetchSurveyData(surveyOverviewFilter);
    };

    // Expose search function via ref if provided
    useEffect(() => {
        if (props.searchFormRef) {
            props.searchFormRef.current = {
                searchWithGroupId: handleSearchWithGroupId,
            };
        }
    }, []);

    const completedSurveyError = intl.formatMessage({
        id: 'survey-overview-completed-error',
        defaultMessage:
            'This Survey is COMPLETED/LIVE and NO edit is possible.  To display the Survey, select the VIEW icon',
    });

    const notStartedSurveyError = intl.formatMessage({
        id: 'survey-overview-not-started-error',
        defaultMessage:
            'This Survey was not Created yet and NO edit is possible.  To Request the Survey, select the Request Survey icon',
    });

    const notStartedAndNewSurveyCountError = intl.formatMessage({
        id: 'survey-overview-maximum-surveys-error',
        defaultMessage: 'You are restricted to a maximum of 15 records per Bulk Survey.',
    });

    const mixedSurveyError = intl.formatMessage({
        id: 'survey-overview-mixed-agreement-error',
        defaultMessage:
            'These eInvoice Agreements are not compatible, please only select entries with the same eInvoice Agreement.',
    });

  const selectSurveyError = intl.formatMessage({
        id: 'survey-overview-Selection-error',
        defaultMessage: 'Please select Survey before you continue',
    });
    const selectCustomerPartnerSurveyError = intl.formatMessage({
        id: 'survey-overview-different-partner-types',
        defaultMessage: 'Different partner types cannot be grouped together. Please edit the partner types separately.',
    });

    const startOrEditSurveyTooltipTitle = intl.formatMessage({
        id: 'survey-overview-startOrEdit-tooltip-title',
        defaultMessage: 'Complete or Edit Survey',
    });

    const viewSurveyTooltipTitle = intl.formatMessage({
        id: 'survey-overview-view-tooltip-title',
        defaultMessage: 'View Survey',
    });

    const cancelSurveyTooltipTitle = intl.formatMessage({
        id: 'survey-overview-cancel-overview-tooltip-title',
        defaultMessage: 'Return to Overview',
    });

    const resetSurveyTooltipTitle = intl.formatMessage({
        id: 'survey-overview-reset-tooltip-title',
        defaultMessage: 'Reset Survey',
    });

    const removeSurveyTooltipTitle = intl.formatMessage({
        id: 'survey-overview-remove-tooltip-title',
        defaultMessage: 'Remove Survey',
    });

    const changeSurveyTooltipTitle = intl.formatMessage({
        id: 'survey-overview-change-tooltip-title',
        defaultMessage: 'Survey Change Request',
    });

    const requestSurveyTooltipTitle = intl.formatMessage({
        id: 'survey-overview-request-tooltip-title',
        defaultMessage: 'Request Survey',
    });

    const copyMasterSurveyTooltipTitle = intl.formatMessage({
        id: 'copy-master-survey-modal-title',
        defaultMessage: 'Copy Master Survey',
    });

    const onClear = () => {
        form.resetFields();
        setPartnerNo(null);
    };

    const onSearch = async () => {
        if (partnerType === 'A') {
            fetchSurveyData({});
            return;
        }

        //console.log(`PartnerType: ${partnerType}`);
        //props.setSurveyLoading(true);
        const values = { ...form.getFieldsValue() };
        // If survey status id is 12 (No Started) then make sure company code was also selected
        //console.log(`Status code: ${values.surveyStatus}`);
        //console.log(`Company Code: ${values.companyCode}`);
        if (values.surveyStatus === '12' && (values.companyCode === undefined || values.companyCode === null)) {
            notification.info({
                message: getI18n(
                    'Survey-overview-company-code-required',
                    "company Code is required when searching for 'NOT STARTED' records",
                    intl
                ),
            });
        } else {
            const surveyOverviewFilter: SurveyOverviewFilter = {};
            //console.log(`From date: ${values.fromDate}`);
            //console.log(`To date: ${values.toDate}`);
            if (values.fromDate !== null && values.fromDate !== undefined && !_.isEmpty(values.fromDate)) {
                surveyOverviewFilter.fromDate = dayjs(values.fromDate).format('YYYY-MM-DD HH:mm:s');
            }
            if (values.toDate !== null && values.toDate !== undefined && !_.isEmpty(values.toDate)) {
                surveyOverviewFilter.toDate = dayjs(values.toDate).format('YYYY-MM-DD HH:mm:s');
            }
            surveyOverviewFilter.companyCode = values.companyCode;
            surveyOverviewFilter.partnerNo = values.partnerNo;
            surveyOverviewFilter.surveyStatusId = Number.parseInt(values.surveyStatus);
            surveyOverviewFilter.userId = values.userId;
            surveyOverviewFilter.partnerType = partnerType;
            surveyOverviewFilter.groupId = values.groupId;
            fetchSurveyData(surveyOverviewFilter);
        }
    };

    const handlePartnerChange = (value: string) => {
        //console.log(`Selected supplier: ${value}`);
        setPartnerNo(value);
    };



    const isValidStateForSurveys = (notStartableSurveyCount, readOnly) => {
        if (notStartableSurveyCount.length > 0 && !readOnly) {
            showErrorNotification(
                getI18n(
                    'survey-overview-not-correct-state-to-start',
                    'COMPLETE OR EDIT SURVEY can only be executed on a MD Record with a status of NEW or IN PROGRESS',
                    intl
                )
            );
            return false;
        }
        return true;
    };



    const isUniqueAgreement = () => {
        const agreementTypeId = props.selectedSurveyIds[0]?.agreementTypeId;
        return props.selectedSurveyIds.every((item) => item.agreementTypeId === agreementTypeId);
    };

    const getSurveyCountByCategory = (categoryCode) => {
        return props.selectedSurveyIds.filter((item) => item.categoryCode === categoryCode).length;
    };


    const extractSurveyMasterDataInput = () => {
        const surveyMasterDataInput = [];
        props.selectedSurveyIds
            .filter((item) => item.groupID === null)
            .forEach((item) => {
                item.includedSurveyIds?.split(',').forEach((surveyId) => {
                    surveyMasterDataInput.push({ surveyId: Number(surveyId) });
                });
                if (surveyMasterDataInput.length === 0) {
                    surveyMasterDataInput.push({ surveyId: Number(item.surveyId) });
                }
            });
        return surveyMasterDataInput;
    };

    const getGroupID = () => {
        const groupIds = props.selectedSurveyIds.filter((item) => item.groupID);
        return groupIds.length > 0 ? groupIds[0].groupID : undefined;
    };

    const handleReadOnlyMode = (groupID) => {
        if (!groupID) {
            notification.error({
                message: 'ERROR',
                description: intl.formatMessage({
                    id: 'survey-overview-error-cannotView-survey',
                    defaultMessage: 'You cannot view a survey that was not created yet',
                }),
            });
        } else {
            localStorage.setItem(SELECTED_GROUP_ID, groupID);
            navigate('/survey/survey-master-data');
        }
    };
    const validateSelectedSurveys = (notStartedCount, readOnly, completedSurvey, totalSupplier:number, totalCustomer:number) => {
        if (notStartedCount.length > 0 && !readOnly) return notStartedSurveyError;
        if (completedSurvey.length > 0 && !readOnly) return completedSurveyError;
        if (totalCustomer > 0 && totalSupplier > 0) return selectCustomerPartnerSurveyError;
        if (!isUniqueAgreement() && !readOnly) return mixedSurveyError;
        if (props.selectedSurveyIds.length === 0) return selectSurveyError;
        return null;
    };
    const handleSurveyDataStorage = (totalCustomer, totalSupplier, readOnly) => {
        if (totalCustomer === props.selectedSurveyIds.length) {
            localStorage.setItem(PARTNER_TYPE, 'C');
        }
        if (totalSupplier === props.selectedSurveyIds.length) {
            localStorage.setItem(PARTNER_TYPE, 'V');
        }
        if (props.selectedSurveyIds && Array.isArray(props.selectedSurveyIds)) {
            const surveyMasterDataInput = extractSurveyMasterDataInput();

            const groupID = getGroupID();
            if (!readOnly) {
                dispatch(setSurveyDataLoading(true));
                Promise.resolve(addSelectedSurveyMasterData(groupID, surveyMasterDataInput))
                    .then((id) => {
                        localStorage.setItem(SELECTED_GROUP_ID, id);
                        navigate('/survey/survey-master-data');
                    })
                    .catch(() => {
                        dispatch(resetSurveyDataLoading(true));
                    })
                    .finally(() => {
                        dispatch(setSurveyDataLoading(false));
                    });
            } else {
                handleReadOnlyMode(groupID);
            }
        }
    };
    const setSelectedSurveys = (surveyMode: MODE) => {
        const companyCodes = props.selectedSurveyIds.map((item) => item.companyCode);
        if (companyCodes.includes('A1') && companyCodes.includes('A1D')) {
            notification.error({
                message: getI18n(
                    'Survey-overview-create-survey-a1-a1d-error',
                    ' The selected company codes cannot be grouped. Please edit the surveys individually.',
                    intl
                ),
            });
            dispatch(setSurveyDataLoading(false));
            return;
        }

        const readOnly = surveyMode === MODE.OVERVIEW || surveyMode === MODE.VIEW;

        const completedSurvey = props.selectedSurveyIds.filter(
            (item) => item.surveyStatusId === 10 || item.surveyStatusId === 9
        );

        const notStartedCount = props.selectedSurveyIds.filter((item) => item.surveyStatusId === 12);
        const notStartableSurveyCount = props.selectedSurveyIds.filter(
            (item) => item.surveyStatusId !== 3 && item.surveyStatusId !== 4
        );

        const newNotStartedCount = props.selectedSurveyIds.filter(
            (item) => item.surveyStatusId === 12 || item.surveyStatusId === 3
        ).length;

        // Notifications
        if (!isValidStateForSurveys(notStartableSurveyCount, readOnly)) return;
        if (newNotStartedCount > 15) {
            showErrorNotification(notStartedAndNewSurveyCountError);
            return;
        }

        const totalSupplier = getSurveyCountByCategory('V');
        const totalCustomer = getSurveyCountByCategory('C');

        const validationErrors = validateSelectedSurveys(notStartedCount, readOnly, completedSurvey, totalSupplier, totalCustomer);
        if (validationErrors) {
            showErrorNotification(validationErrors);
            return;
        }

        handleSurveyDataStorage(totalCustomer, totalSupplier, readOnly);
    };
    const createSurvey = async (action) => {
        dispatch(setSurveyDataLoading(true));
        const selectedRecords: SelectedSurveyGroupID[] = props.selectedSurveyIds;
        const surveyCreateRequests: SurveyCreateRequest[] = [];
        const statusCode = action === 'Change' ? 9 : 12;

        const companyCodes = props.selectedSurveyIds.map((item) => item.companyCode);
        if (companyCodes.includes('A1') && companyCodes.includes('A1D')) {
            notification.error({
                message: getI18n(
                    'Survey-overview-create-survey-a1-a1d-error',
                    ' The selected company codes cannot be grouped. Please edit the surveys individually.',
                    intl
                ),
            });
            dispatch(setSurveyDataLoading(false));
            return;
        }

        selectedRecords
            .filter((item) => item.surveyStatusId === statusCode)
            .forEach((item) => {
                surveyCreateRequests.push({
                    category: item.categoryCode,
                    companyCode: item.companyCode,
                    partnerNo: item.partnerNo,
                    systemCode: item.systemCode,
                    surveyTypeId: 1,
                    groupId: item.groupID,
                    surveyStatusId: item.surveyStatusId,
                });
            });

        if (surveyCreateRequests.length > 0) {
            try {
                const surveyResponse = await createSurveyMutation(surveyCreateRequests);
                let error = false;
                let errorText = '';

                if (Array.isArray(surveyResponse)) {
                    surveyResponse.forEach((surveyItem) => {
                        if (surveyItem.success === '0') {
                            error = true;
                            errorText += ` - \n${surveyItem.errorText}`;
                        }
                    });
                }

                if (!error || errorText === '') {
                    notification.info({
                        message: getI18n('Survey-overview-create-survey-success', 'Survey created successfully', intl),
                    });
                    onSearch();
                } else {
                    notification.error({ message: errorText });
                }
            } catch (error) {
                console.error(error);
                dispatch(resetSurveyDataLoading(true));
                notification.error({
                    message: getI18n(
                        'Survey-overview-create-survey-error',
                        'Backend error, Unable to create Survey',
                        intl
                    ),
                });
            } finally {
                dispatch(setSurveyDataLoading(false));
            }
        } else {
            dispatch(resetSurveyDataLoading(true));
            if (action === 'Change') {
                notification.error({
                    message: getI18n(
                        'Survey-overview-create-survey-invalid-status-change',
                        'CHANGE REQUEST ON SURVEY can only be executed on a MD Record with a status of LIVE',
                        intl
                    ),
                });
            } else {
                notification.error({
                    message: getI18n(
                        'Survey-overview-create-survey-invalid-status',
                        'REQUEST SURVEY can only be executed on a MD Record with a status of NOT STARTED',
                        intl
                    ),
                });
            }
        }
    };

    const startSurvey = () => {
        if (!isOverViewMode()) {
            dispatch(setSurveyMode(MODE.EDIT));
        }
        setSelectedSurveys(MODE.EDIT);
    };

    const viewSurvey = () => {
        if (!isOverViewMode()) {
            dispatch(setSurveyMode(MODE.VIEW));
        }
        setSelectedSurveys(MODE.VIEW);
    };

    const cancelOverviewSurvey = () => {
        dispatch(destroyState(DESTROY_SURVEY_OVERVIEW));
        navigate('/survey/survey-management');
    };

    const changeSurvey = async () => {
        await createSurvey('Change');
    };

    const requestSurvey = async () => {
        await createSurvey('Create');
    };

    const removeSurvey = async () => {
        if (props.selectedSurveyIds && Array.isArray(props.selectedSurveyIds)) {
            if (props.selectedSurveyIds.length === 0) {
                showErrorNotification(
                    getI18n('Survey-overview-select-survey-error', 'Please select Survey before you continue', intl)
                );
                return;
            }

            let notInProgressSurveys = false;
            props.selectedSurveyIds.forEach((item) => {
                if (![4, 3].includes(item.surveyStatusId)) {
                    notInProgressSurveys = true;
                }
            });
            if (notInProgressSurveys) {
                showErrorNotification(
                    getI18n(
                        'Survey-overview-remove-survey-not-possible',
                        'REMOVE SURVEY can only be executed on a record with a status that is not Completed or Live',
                        intl
                    )
                );
                return;
            }

            // props.setSurveyLoading(true);
            dispatch(setSurveyDataLoading(true));
            const cancelSurveyInput = [];
            await props.selectedSurveyIds.forEach((item) =>
                cancelSurveyInput.push({ surveyId: Number(item.surveyID) })
            );

            client
                .mutate({
                    mutation: REMOVE_SELECTED_PARTNER_TO_SURVEY_PROCESS,
                    variables: { surveyIds: cancelSurveyInput },
                    fetchPolicy: 'network-only',
                })
                .then((response) => {
                    notification.success({
                        message: 'SUCCESSFULLY',
                        description: response.data?.Portal_RemoveSurvey,
                    });
                })
                .then((_) => fetchSurveyData(form.getFieldsValue()))
                .catch((err) => {
                    dispatch(resetSurveyDataLoading(true));
                    console.error(err);
                    showErrorNotification(`Error ${err}`);
                })
                .finally(() => {
                    dispatch(setSurveyDataLoading(false));
                });
        }
    };

    useEffect(() => {
        const surveyOverviewCriteria: SurveyOverviewFilter = surveyState.survey.surveyOverviewCriteria;

        if (surveyOverviewCriteria !== undefined && surveyState.survey.surveyMode !== MODE.OVERVIEW) {
            //console.log("set search criteria on survey overview");
            //console.dir(surveyOverviewCriteria);
            let fromDate;
            let toDate;
            let surveyStatus: string;
            if (surveyOverviewCriteria.fromDate !== undefined) {
                fromDate = dayjs(surveyOverviewCriteria.fromDate);
            }
            if (surveyOverviewCriteria.toDate !== undefined) {
                toDate = dayjs(surveyOverviewCriteria.toDate);
            }
            if (surveyOverviewCriteria.surveyStatusId > 0) {
                surveyStatus = surveyOverviewCriteria.surveyStatusId.toString();
            }
            form.setFieldsValue({
                companyCode: surveyOverviewCriteria.companyCode,
                partnerNo: surveyOverviewCriteria.partnerNo,
                surveyStatus,
                userId: surveyOverviewCriteria.userId,
                fromDate,
                toDate,
            });
            if (
                surveyState.survey.managementSearchCriteria !== undefined &&
                surveyState.survey.managementSearchCriteria.partnerNo !== undefined
            ) {
                setPartnerNo(surveyState.survey.managementSearchCriteria.partnerNo);
            }

            //ensure we reset view mode
            if (surveyState.survey.surveyMode === MODE.VIEW) {
                dispatch(setSurveyMode(undefined));
            }

            props.onRefresh();
        }

        //This is supposed to be called when the component unmounts but it does not work as expected
        // return () : void => {
        //     console.log("Destroy survey state");
        //     dispatch(destroyState(DESTROY_SURVEY_OVERVIEW));
        // }
    }, []);

    return (
        <div className={'survey-search-form'}>
            <Form
                size={'middle'}
                layout={'vertical'}
                form={form}
                onFinish={onSearch}
                autoComplete="off"
                className={'mt100'}
            >
                <div hidden={isOverViewMode()} className={'form-grid'}>
                    <RenderIfEntitled
                        entitlementToCheck="edp@systemadmin"
                        intl={props.intl}
                        distinctEntitlements={props.distinctEntitlements}
                    >
                        <div className={'form-column'}>
                            <PartnerLookup
                                intl={props.intl}
                                entitlement={Survey_Overview_Entitlement}
                                onChange={handlePartnerChange}
                                currentUser={props.currentUser}
                                distinctEntitlements={props.distinctEntitlements}
                                colSpan={7}
                            />
                            <div style={{marginTop:"15px"}}>
                                <Form.Item
                                    name={'userId'}
                                    key={'userId'}
                                    label={intl.formatMessage({
                                        id: 'survey-overview-user-id-label',
                                        defaultMessage: 'Simulation External Id',
                                    })}
                                >
                                    <Input
                                        allowClear={true}
                                        placeholder={intl.formatMessage({
                                            id: 'survey-overview-user-id-placeholder',
                                            defaultMessage: 'Enter the User id for data retrieval',
                                        })}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <div className={'form-column'}>
                            <Form.Item
                                name={'fromDate'}
                                key={'fromDate'}
                                label={intl.formatMessage({
                                    id: 'survey-overview-from-date-label',
                                    defaultMessage: 'Date From',
                                })}
                            >
                                <DatePicker
                                    format={getDateFormatOrDefault(props.currentUser)}
                                    placeholder={getI18n(
                                        'survey_overview-from-date-placeholder',
                                        'Survey From Date',
                                        intl
                                    )}
                                    style={{ width: "100%" }}
                                    allowClear={true}
                                    data-testid={'fromDate'}
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    disabledDate={(current) => current.isAfter(dayjs().subtract(0, 'day'))}
                                />
                            </Form.Item>
                            <Form.Item
                                name={'categoryCode'}
                                key={'categoryCode'}
                                label={intl.formatMessage({ id: 'user-profile-grid-category' })}
                            >
                                <Select
                                    onClear={() => setPartnerType(null)}
                                    onChange={setPartnerType}
                                    allowClear={true}
                                    placeholder={intl.formatMessage({ id: 'customers-or-suppliers' })}
                                >
                                    <Select.Option key={'customer-option-C'} value={'C'}>
                                        {intl.formatMessage({
                                            id: 'survey-overview-CustomerOnly',
                                            defaultMessage: 'Customer Only',
                                        })}
                                    </Select.Option>
                                    <Select.Option key={'supplier-option-V'} value={'V'}>
                                        {intl.formatMessage({
                                            id: 'survey-overview-SupplierOnly',
                                            defaultMessage: 'Supplier Only',
                                        })}
                                    </Select.Option>
                                    <Select.Option key={'supplier-option-A'} value={'A'}>
                                        {intl.formatMessage({
                                            id: 'survey-overview-showAll',
                                            defaultMessage: 'Show All',
                                        })}
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </div>
                        <div className={'form-column'}>
                            <Form.Item
                                name={'toDate'}
                                key={'toDate'}
                                label={intl.formatMessage({
                                    id: 'survey-overview-to_date-label',
                                    defaultMessage: 'Date To',
                                })}
                            >
                                <DatePicker
                                    format={getDateFormatOrDefault(props.currentUser)}
                                    placeholder={getI18n(
                                        'survey_overview-date-to-placeholder',
                                        'Survey To Date',
                                        intl
                                    )}
                                    style={{ width: "100%" }}
                                    allowClear={true}
                                    data-testid={'toDate'}
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    disabledDate={(current) =>
                                        current.isBefore(dayjs().subtract(3, 'months')) ||
                                        current.isAfter(dayjs().subtract(0, 'day'))
                                    }
                                />
                            </Form.Item>
                            <Form.Item
                                name={'groupId'}
                                key={'groupId'}
                                label={intl.formatMessage({
                                    id: 'entitlements-master-data-group-group-id-grid-header-name',
                                })}
                            >
                                <Input
                                    allowClear={true}
                                    style={{ width: "100%" }}
                                    placeholder={intl.formatMessage({
                                        id: 'entitlements-master-data-group-group-id-grid-header-name',
                                    })}
                                />
                            </Form.Item>
                        </div>
                        <div className={'form-column'}>
                            <SurveyStatus intl={props.intl} currentUser={props.currentUser} />
                        </div>
                        <div className={'form-column'}>
                            <CompanyLookup
                                intl={props.intl}
                                currentUser={props.currentUser}
                                entitlement={Survey_Overview_Entitlement}
                                distinctEntitlements={props.distinctEntitlements}
                                colSpan={5}
                            />
                        </div>
                    </RenderIfEntitled>
                </div>

                <div hidden={isOverViewMode()} className={'mt50'}>
                    <Row gutter={8}>
                        <RenderIfEntitled
                            entitlementToCheck="edp@systemadmin"
                            intl={props.intl}
                            distinctEntitlements={props.distinctEntitlements}
                        >
                            <div style={{ margin: '20px 0px 10px 0px' }}>
                                <Tooltip
                                    title={intl.formatMessage({ id: 'info-portal-search', defaultMessage: 'Search' })}
                                >
                                    <Button
                                        style={{ marginRight: '10px' }}
                                        data-testid="info-portal-search-button"
                                        type="primary"
                                        onClick={onSearch}
                                        icon={<SearchOutlined />}
                                        size={'large'}
                                    />
                                </Tooltip>

                                <Tooltip title={intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}>
                                    <Button
                                        data-testid="info-portal-clear"
                                        className={styles.simpleFlipRefresh}
                                        type="primary"
                                        icon={<ReloadOutlined />}
                                        onClick={onClear}
                                        size={'large'}
                                    />
                                </Tooltip>
                            </div>
                        </RenderIfEntitled>
                    </Row>
                </div>
                <Row gutter={23} justify="start">
                    <Col xs={24} sm={6} md={4} className="mb5 mt5">
                        <Space align={'start'}>
                            <div hidden={isOverViewMode()}>
                                <RenderIfEntitled
                                    entitlementToCheck="complete@survey"
                                    intl={intl}
                                    distinctEntitlements={props.distinctEntitlements}
                                >
                                    <Tooltip title={requestSurveyTooltipTitle} key={'requestSurvey'}>
                                        <FolderOutlined
                                            style={{ fontSize: '25px', color: '#444444' }}
                                            onClick={requestSurvey}
                                        />
                                    </Tooltip>
                                </RenderIfEntitled>
                            </div>
                            <div hidden={isOverViewMode()}>
                                <RenderIfEntitled
                                    entitlementToCheck="complete@survey"
                                    intl={intl}
                                    distinctEntitlements={props.distinctEntitlements}
                                >
                                    <Tooltip title={startOrEditSurveyTooltipTitle} key={'editStartSurvey'}>
                                        <EditOutlined
                                            style={{
                                                fontSize: '25px',
                                                color: '#444444',
                                                cursor: 'pointer',
                                            }}
                                            onClick={startSurvey}
                                        />
                                    </Tooltip>
                                </RenderIfEntitled>
                            </div>
                            <div hidden={isOverViewMode()}>
                                <RenderIfEntitled
                                    entitlementToCheck="complete@survey"
                                    intl={intl}
                                    distinctEntitlements={props.distinctEntitlements}
                                >
                                    <Tooltip title={changeSurveyTooltipTitle} key={'changeSurvey'}>
                                        <FormOutlined
                                            style={{ fontSize: '25px', color: '#444444' }}
                                            onClick={changeSurvey}
                                        />
                                    </Tooltip>
                                </RenderIfEntitled>
                            </div>
                            <RenderIfEntitled
                                entitlementToCheck="display@survey"
                                intl={intl}
                                distinctEntitlements={props.distinctEntitlements}
                            >
                                <Tooltip title={viewSurveyTooltipTitle} key={'viewSurvey'}>
                                    <FolderViewOutlined
                                        style={{ fontSize: '25px', color: '#444444' }}
                                        onClick={viewSurvey}
                                    />
                                </Tooltip>
                            </RenderIfEntitled>
                            <div hidden={!isOverViewMode()}>
                                <RenderIfEntitled
                                    entitlementToCheck="display@survey"
                                    intl={intl}
                                    distinctEntitlements={props.distinctEntitlements}
                                >
                                    <Tooltip title={cancelSurveyTooltipTitle} key={'cancelOverviewMode'}>
                                        <ArrowLeftOutlined
                                            style={{ fontSize: '25px', color: '#444444' }}
                                            onClick={cancelOverviewSurvey}
                                        />
                                    </Tooltip>
                                </RenderIfEntitled>
                            </div>
                            <div hidden={isOverViewMode()}>
                                <RenderIfEntitled
                                    entitlementToCheck="delete@survey"
                                    intl={intl}
                                    distinctEntitlements={props.distinctEntitlements}
                                >
                                    <Tooltip title={removeSurveyTooltipTitle} key={'removeStartSurvey'}>
                                        <DeleteOutlined
                                            style={{ fontSize: '25px', color: '#444444' }}
                                            onClick={removeSurvey}
                                        />
                                    </Tooltip>
                                </RenderIfEntitled>
                            </div>
                            <div hidden={isOverViewMode()}>
                                <RenderIfEntitled
                                    entitlementToCheck="copy@survey"
                                    intl={intl}
                                    distinctEntitlements={props.distinctEntitlements}
                                >
                                    {props.showCopyButton && (
                                        <Tooltip title={copyMasterSurveyTooltipTitle} key={'copyMasterSurvey'}>
                                            <CopyOutlined
                                                style={{ fontSize: '25px', color: '#444444' }}
                                                onClick={props.copyMasterSurvey}
                                            />
                                        </Tooltip>
                                    )}
                                </RenderIfEntitled>
                            </div>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};
export default SurveyOverviewSearchForm;
